import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import * as d3 from 'd3'
import { ZoomIn, ZoomOut, Maximize2, Download } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button, Tooltip } from '@/components/common'
import type { TreeData, D3TreeNode, TreeConfig } from '@/types'
import { DEFAULT_TREE_CONFIG } from '@/types'

export interface TreeVisualizationProps {
  data: TreeData
  config?: Partial<TreeConfig>
  onNodeClick?: (nodeId: string) => void
  onNodeHover?: (nodeId: string | null) => void
  selectedNodeId?: string | null
  className?: string
}

// Color palette for branch status
const STATUS_COLORS = {
  active: {
    fill: '#a9def9', // icy blue
    stroke: '#3b82f6',
  },
  terminated_natural: {
    fill: '#d0f4de', // frosted mint
    stroke: '#22c55e',
  },
  terminated_manual: {
    fill: '#fcf6bd', // lemon chiffon
    stroke: '#eab308',
  },
  pruned: {
    fill: '#fecaca',
    stroke: '#ef4444',
  },
}

// Branch type indicators
const TYPE_MARKERS = {
  sequential: '→',
  parallel: '⇉',
}

const TreeVisualization = ({
  data,
  config: userConfig,
  onNodeClick,
  onNodeHover,
  selectedNodeId,
  className,
}: TreeVisualizationProps) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 })
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 })

  // Keep handler refs so the d3 effect doesn't reattach listeners every
  // time the parent re-creates the callbacks (which is on every render
  // for arrow functions). This is what was causing the layout to thrash
  // and produce overlapping nodes.
  const onNodeClickRef = useRef(onNodeClick)
  const onNodeHoverRef = useRef(onNodeHover)
  useEffect(() => {
    onNodeClickRef.current = onNodeClick
    onNodeHoverRef.current = onNodeHover
  }, [onNodeClick, onNodeHover])

  // Merge config with defaults — memoized so the d3 effect doesn't run
  // on every render.
  const config = useMemo<TreeConfig>(
    () => ({ ...DEFAULT_TREE_CONFIG, ...userConfig }),
    [userConfig],
  )

  // Handle resize. Clamp to a sane minimum so a `display: none` parent
  // (e.g. closed sidebar) doesn't collapse the SVG to 0×0 and produce
  // a NaN viewBox.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.max(320, Math.floor(entry.contentRect.width))
        const h = Math.max(240, Math.floor(entry.contentRect.height))
        setDimensions((prev) =>
          prev.width === w && prev.height === h ? prev : { width: w, height: h },
        )
      }
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [])
  
  // Build and render tree
  useEffect(() => {
    if (!svgRef.current || !data.nodes.length) return
    
    const svg = d3.select(svgRef.current)
    const { width, height } = dimensions
    const { nodeWidth, nodeHeight, horizontalSpacing, verticalSpacing } = config
    
    // Clear previous content
    svg.selectAll('*').remove()
    
    // Create container group for zoom/pan
    const g = svg.append('g').attr('class', 'tree-container')
    
    // Build hierarchy from flat data
    const hierarchyData = buildHierarchy(data)
    if (!hierarchyData) return
    
    // Create tree layout
    const treeLayout = d3.tree<D3TreeNode>()
      .nodeSize([nodeWidth + horizontalSpacing, nodeHeight + verticalSpacing])
      .separation((a, b) => a.parent === b.parent ? 1 : 1.5)
    
    // Generate tree
    const root = d3.hierarchy(hierarchyData) as d3.HierarchyNode<D3TreeNode>
    const treeData = treeLayout(root)
    
    // Create links
    g.append('g')
      .attr('class', 'links')
      .selectAll('path')
      .data(treeData.links())
      .enter()
      .append('path')
      .attr('class', 'tree-link')
      .attr('d', d3.linkVertical<any, any>()
        .x((d: any) => d.x)
        .y((d: any) => d.y)
      )
      .attr('fill', 'none')
      .attr('stroke', 'var(--color-slate-300)')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6)
    
    // Create node groups
    const nodes = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(treeData.descendants())
      .enter()
      .append('g')
      .attr('class', 'tree-node')
      .attr('transform', (d: any) => `translate(${d.x},${d.y})`)
      .style('cursor', 'pointer')
      .on('click', (event: any, d: any) => {
        event.stopPropagation()
        onNodeClickRef.current?.(d.data.id)
      })
      .on('mouseenter', (_event: any, d: any) => {
        onNodeHoverRef.current?.(d.data.id)
      })
      .on('mouseleave', () => {
        onNodeHoverRef.current?.(null)
      })
    
    // Add node rectangles
    nodes.append('rect')
      .attr('x', -nodeWidth / 2)
      .attr('y', -nodeHeight / 2)
      .attr('width', nodeWidth)
      .attr('height', nodeHeight)
      .attr('rx', 12)
      .attr('ry', 12)
      .attr('fill', (d: any) => {
        const status = d.data.status || 'active'
        return STATUS_COLORS[status as keyof typeof STATUS_COLORS]?.fill || STATUS_COLORS.active.fill
      })
      .attr('stroke', (d: any) => {
        if (d.data.id === selectedNodeId) {
          return '#8b5cf6' // Purple for selected
        }
        const status = d.data.status || 'active'
        return STATUS_COLORS[status as keyof typeof STATUS_COLORS]?.stroke || STATUS_COLORS.active.stroke
      })
      .attr('stroke-width', (d: any) => d.data.id === selectedNodeId ? 3 : 2)
    
    // Add node labels
    nodes.append('text')
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('fill', 'var(--color-slate-700)')
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .text((d: any) => truncateText(d.data.name, 20))
    
    // Add type indicators
    nodes.append('text')
      .attr('x', nodeWidth / 2 - 16)
      .attr('y', -nodeHeight / 2 + 14)
      .attr('font-size', '10px')
      .attr('fill', 'var(--color-slate-500)')
      .text((d: any) => TYPE_MARKERS[d.data.type as keyof typeof TYPE_MARKERS] || '')
    
    // Add confidence indicator
    nodes.filter((d: any) => d.data.confidenceScore > 0)
      .append('circle')
      .attr('cx', nodeWidth / 2 - 8)
      .attr('cy', nodeHeight / 2 - 8)
      .attr('r', 6)
      .attr('fill', (d: any) => {
        const confidence = d.data.confidenceScore
        if (confidence >= 0.8) return '#22c55e'
        if (confidence >= 0.5) return '#eab308'
        return '#ef4444'
      })
    
    // Setup zoom behavior. We keep ONE zoom instance per SVG (in
    // `zoomRef`) so the controls below operate on the same behavior the
    // user is panning/zooming with. Previously each control instantiated
    // a brand-new `d3.zoom()`, which silently no-op'd because it was
    // never attached to the SVG.
    let zoom = zoomRef.current
    if (!zoom) {
      zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 3])
        .on('zoom', (event) => {
          // Re-select the container live so this works across re-renders.
          d3.select(svgRef.current)
            .select<SVGGElement>('g.tree-container')
            .attr('transform', event.transform.toString())
          setTransform({
            x: event.transform.x,
            y: event.transform.y,
            k: event.transform.k,
          })
        })
      zoomRef.current = zoom
      svg.call(zoom)
    }

    // Center the tree on initial render / data change.
    const bounds = (g.node() as SVGGElement)?.getBBox()
    if (bounds && bounds.width > 0 && bounds.height > 0) {
      const scale = Math.min(
        (width - 100) / bounds.width,
        (height - 100) / bounds.height,
        1,
      )
      const dx = width / 2 - (bounds.x + bounds.width / 2) * scale
      const dy = height / 2 - (bounds.y + bounds.height / 2) * scale
      svg.call(zoom.transform, d3.zoomIdentity.translate(dx, dy).scale(scale))
    }

  }, [data, dimensions, config, selectedNodeId])

  // Zoom controls — operate on the SAME zoom behavior attached above.
  const handleZoomIn = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return
    d3.select(svgRef.current).transition().duration(200).call(
      zoomRef.current.scaleBy as any,
      1.3,
    )
  }, [])

  const handleZoomOut = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return
    d3.select(svgRef.current).transition().duration(200).call(
      zoomRef.current.scaleBy as any,
      1 / 1.3,
    )
  }, [])

  const handleFitView = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return
    const svg = d3.select(svgRef.current)
    const g = svg.select<SVGGElement>('g.tree-container')
    const bounds = (g.node() as SVGGElement | null)?.getBBox()

    if (bounds && bounds.width > 0 && bounds.height > 0) {
      const { width, height } = dimensions
      const scale = Math.min(
        (width - 100) / bounds.width,
        (height - 100) / bounds.height,
        1,
      )
      const dx = width / 2 - (bounds.x + bounds.width / 2) * scale
      const dy = height / 2 - (bounds.y + bounds.height / 2) * scale

      svg.transition().duration(500).call(
        zoomRef.current.transform as any,
        d3.zoomIdentity.translate(dx, dy).scale(scale),
      )
    }
  }, [dimensions])
  
  const handleExportSVG = useCallback(() => {
    if (!svgRef.current) return
    
    const svgData = new XMLSerializer().serializeToString(svgRef.current)
    const blob = new Blob([svgData], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = url
    a.download = 'conversation-tree.svg'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])
  
  // Empty state
  if (!data.nodes.length) {
    return (
      <div className={cn('flex items-center justify-center h-64 text-slate-500', className)}>
        <p>No branches yet. Start by describing your situation.</p>
      </div>
    )
  }
  
  return (
    <div ref={containerRef} className={cn('relative w-full h-full min-h-[400px]', className)}>
      {/* Controls */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <Tooltip content="Zoom in">
          <Button variant="secondary" size="sm" onClick={handleZoomIn}>
            <ZoomIn className="w-4 h-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Zoom out">
          <Button variant="secondary" size="sm" onClick={handleZoomOut}>
            <ZoomOut className="w-4 h-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Fit to view">
          <Button variant="secondary" size="sm" onClick={handleFitView}>
            <Maximize2 className="w-4 h-4" />
          </Button>
        </Tooltip>
        <Tooltip content="Export SVG">
          <Button variant="secondary" size="sm" onClick={handleExportSVG}>
            <Download className="w-4 h-4" />
          </Button>
        </Tooltip>
      </div>
      
      {/* Zoom indicator */}
      <div className="absolute bottom-4 left-4 z-10 text-xs text-slate-500 bg-white/80 dark:bg-slate-800/80 px-2 py-1 rounded">
        {Math.round(transform.k * 100)}%
      </div>
      
      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="bg-slate-50/50 dark:bg-slate-800/30 rounded-xl"
      />
      
      {/* Legend */}
      <div className="absolute bottom-4 right-4 z-10 flex gap-3 bg-white/90 dark:bg-slate-800/90 px-3 py-2 rounded-lg text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: STATUS_COLORS.active.fill }} />
          <span>Active</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: STATUS_COLORS.terminated_natural.fill }} />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: STATUS_COLORS.terminated_manual.fill }} />
          <span>Terminated</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: STATUS_COLORS.pruned.fill }} />
          <span>Pruned</span>
        </div>
      </div>
    </div>
  )
}

// Helper: Build hierarchy from flat node list
function buildHierarchy(data: TreeData): D3TreeNode | null {
  if (!data.nodes.length) return null
  
  const nodeMap = new Map<string, D3TreeNode>()
  
  // Create D3TreeNode objects. We spread the source TreeNode so all required
  // fields (label, branchId, branchStatus, etc.) flow through, then add D3
  // layout placeholders. Cast at the boundary to satisfy the structural
  // intersection of TreeNode + D3 layout fields.
  for (const node of data.nodes) {
    const d3Node = {
      ...node,
      x: 0,
      y: 0,
      children: [] as D3TreeNode[],
      data: node,
    } as unknown as D3TreeNode
    nodeMap.set(node.id, d3Node)
  }
  
  // Build parent-child relationships
  let root: D3TreeNode | null = null
  
  for (const edge of data.edges) {
    const parent = nodeMap.get(edge.source)
    const child = nodeMap.get(edge.target)
    
    if (parent && child) {
      parent.children = parent.children || []
      parent.children.push(child)
    }
  }
  
  // Find root (node with no incoming edges)
  const childIds = new Set(data.edges.map((e) => e.target))
  for (const node of data.nodes) {
    if (!childIds.has(node.id)) {
      root = nodeMap.get(node.id) || null
      break
    }
  }
  
  // If no root found, use first node
  if (!root && data.nodes.length > 0) {
    root = nodeMap.get(data.nodes[0].id) || null
  }
  
  return root
}

// Helper: Truncate text
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

export default TreeVisualization
